import { useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Search, Trash2, Upload } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Assets() {
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    fetchAssets();
  }, []);

  useEffect(() => {
    let filtered = assets;

    if (searchTerm) {
      filtered = filtered.filter((asset) =>
        asset.asset_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter((asset) => asset.asset_type === typeFilter);
    }

    setFilteredAssets(filtered);
  }, [searchTerm, typeFilter, assets]);

  const fetchAssets = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/assets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssets(response.data);
      setFilteredAssets(response.data);
    } catch (error) {
      toast.error('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/assets/${assetId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Asset deleted successfully');
      fetchAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'image':
        return 'bg-blue-100 text-blue-700';
      case 'voice':
        return 'bg-purple-100 text-purple-700';
      case 'element':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return <div className="text-center py-8">Loading assets...</div>;
  }

  return (
    <div data-testid="assets-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Assets Management</h1>
        <p className="text-slate-600">Manage all project assets</p>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              data-testid="asset-search-input"
              type="text"
              placeholder="Search by asset name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="relative z-20">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="asset-type-filter" className="w-40">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="z-50">
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="voice">Voice</SelectItem>
                <SelectItem value="element">Element</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {filteredAssets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">No assets found</p>
            <p className="text-sm text-slate-400 mt-2">Configure Google Cloud Storage credentials to start uploading assets</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="assets-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Asset Name</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">File Size</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Uploaded By</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Uploaded Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssets.map((asset) => (
                  <tr key={asset.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`asset-row-${asset.id}`}>
                    <td className="py-3 px-4 text-sm font-medium text-slate-700">{asset.asset_name}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getTypeColor(asset.asset_type)}`}>
                        {asset.asset_type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">{formatFileSize(asset.file_size)}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{asset.uploaded_by}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">
                      {format(new Date(asset.uploaded_at), 'MMM dd, yyyy')}
                    </td>
                    <td className="py-3 px-4">
                      <Button
                        data-testid={`delete-asset-button-${asset.id}`}
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteAsset(asset.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
